import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * PageDebugger - Development helper to log current page information to console
 * Only active in development mode
 */
export default function PageDebugger() {
  const location = useLocation();

  useEffect(() => {
    const pageName = getPageNameFromPath(location.pathname);
    const timestamp = new Date().toLocaleTimeString();
    
    
    // Add data attributes to document for Elements tab visibility
    // These work in both dev and production, but console logs are dev-only
    try {
      document.documentElement.setAttribute('data-current-page', pageName);
      document.documentElement.setAttribute('data-current-path', location.pathname);
      document.body.setAttribute('data-page', pageName);
      document.body.setAttribute('data-path', location.pathname);
      
      // Add to #root element as well
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.setAttribute('data-current-page', pageName);
        rootElement.setAttribute('data-current-path', location.pathname);
      }

    } catch (error) {
      console.error('❌ Error setting data attributes:', error);
    }
    
    // Only log in development mode
    if (import.meta.env.DEV) {
      
      // Log page information with styled console output
      console.group(
        `%c📍 Page Navigation - ${timestamp}`,
        'background: #3b82f6; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
      );            
      
      console.groupEnd();
    }
    
    // Add to window for easy debugging (works in all modes)
    window.__CURRENT_PAGE__ = {
      name: pageName,
      path: location.pathname,
      search: location.search,
      state: location.state,
      fullLocation: location,
    };
  }, [location]);

  // Optionally render a visual indicator in development mode
  if (!import.meta.env.DEV) return null;
  
  const pageName = getPageNameFromPath(location.pathname);
  
  return (
    <div
      style={{
        position: 'fixed',
        bottom: '10px',
        right: '10px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '8px 12px',
        borderRadius: '8px',
        fontSize: '11px',
        fontWeight: '600',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        zIndex: 999999,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        pointerEvents: 'none',
        opacity: 0.85,
        transition: 'opacity 0.3s ease',
      }}
      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.85'}
    >
      <span style={{ fontSize: '14px' }}>📍</span>
      <span>{pageName}</span>
      <span style={{ 
        fontSize: '9px', 
        opacity: 0.8,
        marginLeft: '4px',
        background: 'rgba(255,255,255,0.2)',
        padding: '2px 6px',
        borderRadius: '4px'
      }}>
        DEV
      </span>
    </div>
  );
}

/**
 * Helper function to extract page name from pathname
 */
function getPageNameFromPath(pathname) {
  // Remove leading/trailing slashes
  const cleanPath = pathname.replace(/^\/|\/$/g, '');
  
  if (!cleanPath || cleanPath === '') {
    return 'Dashboard (Home)';
  }
  
  // Handle dynamic routes like /Jobs/:id
  const parts = cleanPath.split('/');
  
  if (parts[0] === 'Jobs' && parts[1]) {
    return `Job Detail (ID: ${parts[1]})`;
  }
  
  // Capitalize first letter and handle special cases
  const pageName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
  
  return pageName;
}

/**
 * Helper function to manually log page info (can be called from anywhere)
 * Usage: import { logPageInfo } from '@/lib/PageDebugger';
 */
export function logPageInfo(customMessage) {
  if (import.meta.env.DEV && window.__CURRENT_PAGE__) {
    console.group(`%c🔍 ${customMessage || 'Page Info'}`, 'color: #8b5cf6; font-weight: bold;');
    console.table(window.__CURRENT_PAGE__);
    console.groupEnd();
  }
}

/**
 * Hook to get current page info programmatically
 */
export function usePageInfo() {
  const location = useLocation();
  
  return {
    name: getPageNameFromPath(location.pathname),
    path: location.pathname,
    search: location.search,
    state: location.state,
    fullLocation: location,
  };
}
